// Eventy (T4 + T25): tworzenie, skład z weryfikacją dokumentów, status, szczegóły
// z rozliczonymi godzinami; u pracownika "Moje eventy" z przyciskiem "Rozlicz godziny".
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Badge, PersonCell, Modal, fullName, useDateFilter, DateFilter, useToast, RoleBadge, EmployeeFilter, uniqueUsers, useSort, SortTh, ROLE_LABELS, isDirty, AddedOn, ReviewedBy, RejectedNote, TimeSelect, askRejectReason, ScopeTabs, fmtDay, BONUS_LABELS, BONUS_NEEDS_DESC, BONUS_MIN_DESC, BONUS_MAX_AMOUNT, TeamDiffBadge, teamDiffs } from '../components/UI';
import api from '../utils/api';
import { PersonEventsModal } from '../components/PaySpec';

// „W trakcie" wycofane w 1.3 (T71) — stare eventy z tym statusem nadal się wyświetlają
const EVENT_STATUSES = [['PLANNED', 'Zaplanowany'], ['CONFIRMED', 'Potwierdzony'], ['POSTPONED', 'Przełożony'], ['COMPLETED', 'Zrealizowany'], ['CANCELLED', 'Anulowany']];
const EVENT_SORT = {
  date: ev => String(ev.date),
  name: ev => ev.name,
  location: ev => ev.location,
  team: ev => ev.assignments?.length || 0,
  status: ev => ev.status,
};
const todayKey = () => new Date().toISOString().slice(0, 10);
const dateKey = (d) => String(d).slice(0, 10);
const emptyForm = () => ({ name: '', date: todayKey(), startTime: '10:00', endTime: '20:00', location: '', notes: '', status: 'PLANNED', requiresDocs: false, team: {}, roles: {} }); // team: { userId: paymentType }; roles: { userId: { leader, driver } } (T138)
const fmtDate = (d) => new Date(d).toLocaleDateString('pl-PL');
const LICENSE_CATEGORIES = ['B', 'B+E', 'C', 'C+E', 'D', 'T'];
// T65: domyślnie pokazujemy tych, którzy zadeklarowali dostępność i są wolni;
// resztę widać po rozwinięciu „Bez deklaracji"
// T58: płeć, wiek (na dzień eventu), sprawdzeni (≥ 5 odbytych eventów albo „doświadczony” w profilu — T170), ostatnio razem z już wybranymi
const emptyFilters = () => ({ q: '', free: false, docs: false, license: '', avail: 'declaredFree', gender: '', age: '', veteran: false, together: false });
const VETERAN_MIN = 5;

// Dokumenty pracownika na dzień eventu
const docStatus = (u, date) => {
  const check = (has, exp) => !has ? 'brak' : (exp && new Date(exp) < new Date(date) ? 'wygasła' : 'ok');
  return { kamilkowa: check(u.hasKamilkowa, u.kamilkowaExpiryDate), niekaralnosc: check(u.hasNiekaralnosc, u.niekaralnoscExpiryDate) };
};
const DocBadge = ({ label, state }) => (
  <span style={{ fontSize: 10, fontWeight: 600, color: state === 'ok' ? '#059669' : '#DC2626' }} title={`${label}: ${state}`}>
    {state === 'ok' ? '✓' : '✗'} {label}
  </span>
);

export default function EventsPage() {
  const { user, isAdmin, canManageEvents, canAssignEvents, canReviewHours, canSeePay } = useAuth();
  const [personCard, setPersonCard] = useState(null); // T91
  const [searchParams, setSearchParams] = useSearchParams();
  const [settleFor, setSettleFor] = useState(null);   // T86: { ev, entry } — okno rozliczenia godzin na miejscu
  const [expenseEdit, setExpenseEdit] = useState(null); // T153: { ev | null, bonus } — poprawa odrzuconego wydatku / dodatku
  const [expenseFor, setExpenseFor] = useState(null); // T86: event, pod którym zgłaszamy wydatek; { general: true } = dodatek spoza eventu (T96)
  const [myBonuses, setMyBonuses] = useState([]);     // T96: moje dodatki spoza eventów
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);      // kandydaci do składu (tylko dla układających)
  const [dayEvents, setDayEvents] = useState([]); // inne eventy w dniu z formularza — do wykrywania konfliktów przy zaznaczaniu
  const [filters, setFilters] = useState(emptyFilters()); // filtry listy kandydatów (łączą się)
  const [availableIds, setAvailableIds] = useState({}); // userId → true: zadeklarował dzień z formularza (T57)
  const [editing, setEditing] = useState(null); // null = brak okna; {} = nowy; event = edycja
  const [form, setForm] = useState(emptyForm());
  const [formStart, setFormStart] = useState(emptyForm()); // stan początkowy (T62)
  const [details, setDetails] = useState(null);
  const [showOthers, setShowOthers] = useState(false); // T65: rozwinięta lista osób bez deklaracji
  const [onlyTeam, setOnlyTeam] = useState(false);     // T159: w edycji — tylko przypisani
  const [showNotes, setShowNotes] = useState(null);
  const [declining, setDeclining] = useState(null); // T64: okno „nie mogę" z powodem
  const [declineReason, setDeclineReason] = useState('');
  const [noteForm, setNoteForm] = useState({ content: '', rating: 5, equipmentNotes: '' });
  const [editNote, setEditNote] = useState(null); // T166: poprawiana uwaga (przez godzinę od wpisania)
  const [tab, setTab] = useState('upcoming'); // upcoming | past | all
  const [dmuch, setDmuch] = useState(null);     // T156: stan integracji z DmuchApp (dla zarządzających eventami)
  const [dmuchBusy, setDmuchBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState('');
  const df = useDateFilter('year');
  const { show, ToastComponent } = useToast();

  const buildsTeams = canAssignEvents;   // układa skład (T69)
  // Widzi wszystkie eventy z rozliczeniem składu: układający skład i rozpatrujący godziny (T77)
  const seesAllEvents = buildsTeams || canReviewHours;
  // T147 (19.10): organizator / opiekun, który sam jeździ na eventy — przełącznik „Moje / Zespół” (jak w Godzinach pracy)
  const worksEvents = !!user?.eventWorkEnabled && user?.role !== 'EVENT';
  const [view, setView] = useState('mine');
  const splitView = seesAllEvents && worksEvents;
  const seesAllEv = seesAllEvents && (!splitView || view === 'team');
  // T77: cudze oczekujące wpisy pod eventem — do zatwierdzenia przez opiekuna / przełożonego
  const pendingOf = (ev) => [...(ev.workHours || []), ...(ev.bonuses || [])].filter(x => x.status === 'PENDING' && x.userId !== user?.id && !x.settlementId);
  const createsEvents = canManageEvents; // tworzy, edytuje dane eventu, zmienia status
  const isMine = (ev) => (ev.assignments || []).some(a => a.user?.id === user?.id);

  const people = uniqueUsers(events.flatMap(ev => (ev.assignments || []).map(a => a.user)));
  const today = todayKey();
  // T86: pracownik ma zakładki wg swojego rozliczenia, układający skład — wg czasu
  const myH = (ev) => (ev.workHours || []).filter(h => h.userId === user?.id);
  const myB = (ev) => (ev.bonuses || []).filter(b => b.userId === user?.id);
  // 12.10: rozliczenie i uwagi od dnia PO evencie (w dniu eventu jeszcze trwa praca)
  // 12.10: rozliczenie od 2 h po końcu eventu (settleFrom liczy serwer, w strefie PL)
  const settleOpen = (ev) => ev.settleFrom ? new Date(ev.settleFrom) <= new Date() : dateKey(ev.date) < today;
  // T148: odrzucony wpis nie rozlicza eventu — event wraca do „Do rozliczenia” z czerwoną informacją
  const myActive = (ev) => myH(ev).filter(h => h.status !== 'REJECTED');
  const myRejected = (ev) => myActive(ev).length === 0 ? myH(ev).find(h => h.status === 'REJECTED') : null;
  // T153: zakładka „Odrzucone” — odrzucone godziny (bez nowego wpisu) albo odrzucony wydatek
  const myRejB = (ev) => myB(ev).filter(b => b.status === 'REJECTED');
  const hasRejected = (ev) => isMine(ev) && (!!myRejected(ev) || myRejB(ev).length > 0);
  const isTodo = (ev) => isMine(ev) && settleOpen(ev) && ev.status !== 'CANCELLED' && myActive(ev).length === 0;
  const TABS = seesAllEv
    ? [['upcoming', 'Nadchodzące'], ['past', 'Odbyte'], ...(canReviewHours ? [['review', `Do zatwierdzenia (${events.filter(ev => pendingOf(ev).length).length})`]] : []), ['all', 'Wszystkie']]
    : [['upcoming', 'Nadchodzące'], ['todo', `Do rozliczenia (${events.filter(isTodo).length})`], ['settled', 'Rozliczone'], ['rejected', `Odrzucone (${events.filter(hasRejected).length})`], ['all', 'Wszystkie']];
  const visible = events
    .filter(ev => seesAllEv || isMine(ev)) // T147: widok „Moje”
    .filter(ev => !employee || (ev.assignments || []).some(a => a.user?.id === employee))
    .filter(ev => {
      if (tab === 'all') return true;
      if (tab === 'todo') return isTodo(ev);
      if (tab === 'rejected') return hasRejected(ev); // T153
      if (tab === 'review') return pendingOf(ev).length > 0;
      if (tab === 'settled') return myActive(ev).length > 0;
      const past = dateKey(ev.date) < today || ['COMPLETED', 'CANCELLED'].includes(ev.status);
      return tab === 'past' ? past : !past;
    });
  const es = useSort(visible, EVENT_SORT, 'date', ['past', 'settled', 'todo', 'review', 'rejected'].includes(tab) ? 'desc' : 'asc');
  // Suma moich zarobków w widoku (zakładka „Rozliczone") — bez odrzuconych
  const viewSum = (tab === 'settled' || tab === 'all') && !seesAllEv ? visible.reduce((acc, ev) => {
    for (const x of [...myH(ev).map(h => ({ s: h.status, a: Number(h.totalAmount) || 0 })), ...myB(ev).map(b => ({ s: b.status, a: Number(b.amount) || 0 }))]) {
      if (x.s === 'APPROVED') acc.ok += x.a; else if (x.s === 'PENDING') acc.wait += x.a;
    }
    return acc;
  }, { ok: 0, wait: 0 }) : null;

  const load = useCallback(async () => {
    try { setEvents(await api.getEvents(`from=${df.from}&to=${df.to}`)); } catch (e) { show(e.message, 'error'); }
    // T96: eventowy nie ma „Godzin pracy" — jego dodatki spoza eventów pokazujemy tutaj
    if (user?.role === 'EVENT') api.getBonuses(`from=${df.from}&to=${df.to}`).then(b => setMyBonuses(b.filter(x => x.userId === user.id && !x.eventId))).catch(() => {});
    setLoading(false);
  }, [df.from, df.to]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canManageEvents) api.getDmuchappStatus().then(setDmuch).catch(() => {}); }, [canManageEvents]);
  const syncDmuch = async () => { // T156
    setDmuchBusy(true);
    try { const r = await api.syncDmuchapp(); show(r.message); load(); } catch (e) { show(e.message, 'error'); }
    api.getDmuchappStatus().then(setDmuch).catch(() => {});
    setDmuchBusy(false);
  };
  // T147: bez własnych eventów od razu widok „Zespół” (pusty „Moje” nic nie mówi)
  const [viewPicked, setViewPicked] = useState(false);
  useEffect(() => {
    if (!splitView || loading || viewPicked) return;
    setViewPicked(true);
    if (!events.some(isMine)) setView('team');
  }, [splitView, loading, events]); // eslint-disable-line
  // Wejście z Dashboardu „Rozlicz" → /events?settle=ID — otwórz okno rozliczenia (T86)
  useEffect(() => { // T153: /events?tab=rejected (z Dashboardu)
    const t = searchParams.get('tab');
    if (t !== 'rejected') return;
    setSearchParams({}, { replace: true }); setView('mine'); setViewPicked(true); setTab('rejected');
  }, [searchParams]); // eslint-disable-line
  useEffect(() => {
    const id = searchParams.get('settle');
    if (!id || loading) return;
    setSearchParams({}, { replace: true });
    const ev = events.find(e => e.id === id);
    if (ev) { setView('mine'); setViewPicked(true); setTab(myRejected(ev) ? 'rejected' : 'todo'); setSettleFor({ ev, entry: myRejected(ev) || null }); } // T148: odrzucony — poprawa tego wpisu
    else api.getEvent(id).then(e => setSettleFor({ ev: e, entry: null })).catch(err => show(err.message, 'error'));
  }, [searchParams, loading, events]);


  // Konflikty sprawdzamy w momencie zaznaczania (nie dopiero przy zapisie): eventy z dnia formularza
  useEffect(() => {
    if (!editing || !form.date) { setDayEvents([]); return; }
    let cancelled = false;
    api.getEvents(`from=${form.date}&to=${form.date}`)
      .then(list => { if (!cancelled) setDayEvents(list.filter(ev => ev.id !== editing.id && ev.status !== 'CANCELLED')); })
      .catch(() => setDayEvents([]));
    api.getAvailability(form.date, form.date)
      .then(rows => { if (!cancelled) setAvailableIds(Object.fromEntries(rows.map(r => [r.user.id, r]))); })
      .catch(() => setAvailableIds({}));
    // Kandydaci z całej puli eventowej, z wiekiem na dzień eventu i historią (T58)
    api.getEventCandidates(form.date)
      .then(list => { if (!cancelled) setUsers(list); })
      .catch(() => setUsers([]));
    return () => { cancelled = true; };
  }, [editing, form.date]);
  // userId → event, na którym ta osoba już jest tego dnia
  const busy = {};
  dayEvents.forEach(ev => (ev.assignments || []).forEach(a => { if (a.user?.id && !busy[a.user.id]) busy[a.user.id] = ev; }));
  const busyText = (ev) => [ev.location, fmtDate(ev.date), ev.startTime ? `${ev.startTime}–${ev.endTime || '?'}` : null].filter(Boolean).join(', ');

  // T77: zatwierdzanie godzin składu w szczegółach — okno zostaje otwarte, dane odświeżone
  const reviewInDetails = async (fn, okMessage) => {
    try {
      const r = await fn(); show((r && r.message) || okMessage); load();
      if (details) setDetails(await api.getEvent(details.id));
    } catch (e) { show(e.message, 'error'); }
  };

  const act = async (fn, okMessage) => {
    try { const r = await fn(); show((r && r.message) || okMessage); load(); if (details) setDetails(null); }
    catch (e) { show(e.message, 'error'); }
  };

  // ---- formularz ----
  const openNew = () => { const f = emptyForm(); setForm(f); setFormStart(f); setFilters(emptyFilters()); setOnlyTeam(false); setEditing({}); };
  const openEdit = (ev) => {
    setForm({
      name: ev.name, date: dateKey(ev.date), startTime: ev.startTime || '', endTime: ev.endTime || '',
      location: ev.location || '', notes: ev.notes || '', status: ev.status, requiresDocs: !!ev.requiresDocs,
      team: Object.fromEntries((ev.assignments || []).map(a => [a.user.id, a.paymentType || 'hourly'])),
      roles: rolesOf(ev),
    });
    setFormStart({
      name: ev.name, date: dateKey(ev.date), startTime: ev.startTime || '', endTime: ev.endTime || '',
      location: ev.location || '', notes: ev.notes || '', status: ev.status, requiresDocs: !!ev.requiresDocs,
      team: Object.fromEntries((ev.assignments || []).map(a => [a.user.id, a.paymentType || 'hourly'])),
      roles: rolesOf(ev),
    });
    setFilters(emptyFilters());
    setOnlyTeam((ev.assignments || []).length > 0); // T159: event ze składem otwiera się na „W składzie”
    setEditing(ev);
  };
  const closeForm = () => setEditing(null);
  // Domyślnie rozliczenie godzinowe; osoba już na innym evencie tego dnia → pytanie od razu (T52)
  const toggleMember = (u) => {
    if (!form.team[u.id] && busy[u.id]) {
      const ev = busy[u.id];
      const ok = window.confirm(`Uwaga: ${fullName(u)} jest już przydzielony na event „${ev.name}" (${busyText(ev)}).\n\nCzy na pewno chcesz go dodać także tutaj?`);
      if (!ok) return;
    }
    setForm(f => {
      const team = { ...f.team };
      const roles = { ...(f.roles || {}) };
      if (team[u.id]) { delete team[u.id]; delete roles[u.id]; } else team[u.id] = 'hourly';
      return { ...f, team, roles };
    });
  };
  const setPayment = (id, v) => setForm(f => ({ ...f, team: { ...f.team, [id]: v } }));
  // T138: lider / kierowca — przełącznik przy osobie w składzie
  const toggleRole = (id, key) => setForm(f => {
    const cur = { leader: false, driver: false, animator: false, ...((f.roles || {})[id] || {}) };
    cur[key] = !cur[key];
    return { ...f, roles: { ...(f.roles || {}), [id]: cur } };
  });

  const submit = async (allowConflicts = false, team = form.team) => {
    const assignments = Object.entries(team).map(([userId, paymentType]) => ({ userId, paymentType, isLeader: !!form.roles?.[userId]?.leader, isDriver: !!form.roles?.[userId]?.driver, isAnimator: !!form.roles?.[userId]?.animator }));
    // Kto ma tylko „przypisuje do eventów" — wysyła wyłącznie skład (backend i tak pilnuje) — T69
    const payload = createsEvents ? {
      name: form.name, date: form.date, startTime: form.startTime || null, endTime: form.endTime || null,
      location: form.location, notes: form.notes, status: form.status, requiresDocs: form.requiresDocs,
      allowConflicts, assignments,
    } : { allowConflicts, assignments };
    try {
      if (editing && editing.id) { await api.updateEvent(editing.id, payload); show('Event zapisany'); }
      else { await api.createEvent(payload); show('Event utworzony'); }
      closeForm(); load();
    } catch (e) {
      if (e.status === 409 && e.data?.conflicts?.length) {
        // T52: osobne pytanie o każdą osobę; "Anuluj" = wypadnie ze składu, reszta zostaje zapisana
        const kept = { ...team };
        for (const c of e.data.conflicts) {
          const when = [c.location, fmtDate(c.date), c.startTime ? `${c.startTime}–${c.endTime || '?'}` : null].filter(Boolean).join(', ');
          const ok = window.confirm(`Uwaga: ${c.name} jest już przydzielony na event „${c.event}" (${when}).\n\nCzy na pewno chcesz go dodać także tutaj?`);
          if (!ok) delete kept[c.userId];
        }
        setForm(f => ({ ...f, team: kept }));
        return submit(true, kept);
      }
      show(e.message, 'error');
    }
  };

  const handleDelete = (ev) => {
    if (!window.confirm(`Usunąć event „${ev.name}" (${new Date(ev.date).toLocaleDateString('pl-PL')})?`)) return;
    act(() => api.deleteEvent(ev.id), 'Event usunięty');
  };

  const submitNote = async () => {
    try {
      if (editNote) await api.updateEventNote(showNotes.id, editNote.id, noteForm); else await api.addEventNote(showNotes.id, noteForm);
      show(editNote ? 'Uwagi poprawione!' : 'Uwagi zapisane!'); setShowNotes(null); setEditNote(null); setNoteForm({ content: '', rating: 5, equipmentNotes: '' }); load();
    }
    catch (e) { show(e.message, 'error'); }
  };

  // T64: moje przypisanie do eventu (potwierdzenie / odmowa)
  const myAssignment = (ev) => (ev.assignments || []).find(a => a.user?.id === user?.id);
  const confirmMine = (ev) => act(() => api.confirmEvent(ev.id), 'Udział potwierdzony');
  const doDecline = () => {
    const ev = declining; const reason = declineReason;
    setDeclining(null); setDeclineReason('');
    act(() => api.declineEvent(ev.id, reason), 'Zgłoszone');
  };

  // Pracownik ze składu: rozliczenie godzin pod eventem — okno na miejscu (T86; wcześniej przejście do Godzin)
  const settleHours = (ev) => setSettleFor({ ev, entry: null });
  const canSettle = (ev) => isMine(ev) && ev.status !== 'CANCELLED' && settleOpen(ev); // od 2 h po evencie (12.10)
  const myHoursFor = myH;

  // Kandydaci do składu: eventowi i wszyscy z włączoną pracą eventową; filtry łączą się (AND).
  // Zaznaczeni zawsze zostają na liście, żeby filtr nie "gubił" składu.
  const q = filters.q.trim().toLowerCase();
  const hasLicense = (u, cat) => !cat || String(u.drivingLicense || '').split(',').map(x => x.trim()).includes(cat);
  const byName = (a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'pl');
  const pool = users.filter(u => u.role === 'EVENT' || u.eventWorkEnabled);
  // „Ostatnio razem": ile razy osoba pracowała z już zaznaczonymi (ostatnie pół roku)
  const chosenIds = Object.keys(form.team);
  const togetherScore = (u) => chosenIds.reduce((sum, id) => sum + ((u.together || {})[id] || 0), 0);
  const matches = (u) => (!q || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q))
    && (!filters.free || !busy[u.id])
    && (!filters.docs || (docStatus(u, form.date).kamilkowa === 'ok' && docStatus(u, form.date).niekaralnosc === 'ok'))
    && hasLicense(u, filters.license)
    && (!filters.gender || u.gender === filters.gender)
    && (!filters.age || (filters.age === 'adult' ? u.adult === true : u.adult === false))
    && (!filters.veteran || u.experienced || (u.completedEvents || 0) >= VETERAN_MIN)
    && (!filters.together || togetherScore(u) > 0);
  const availOk = (u) => (filters.avail !== 'declared' || availableIds[u.id])
    && (filters.avail !== 'declaredFree' || (availableIds[u.id] && !busy[u.id]));
  // Przy „ostatnio razem" najczęstsi współpracownicy na górze
  const sorter = filters.together ? (a, b) => (togetherScore(b) - togetherScore(a)) || byName(a, b) : byName;
  const inTeam = pool.filter(u => form.team[u.id]).sort(byName);
  const restMatching = pool.filter(u => !form.team[u.id] && matches(u) && availOk(u)).sort(sorter);
  const noBirthDate = filters.age ? pool.filter(u => u.adult === null).length : 0;
  const noGender = filters.gender ? pool.filter(u => !u.gender).length : 0;
  // Reszta — pasuje do filtrów, ale nie zadeklarowała dostępności (T65): pod zwijanym nagłówkiem
  const others = filters.avail && !onlyTeam ? pool.filter(u => !form.team[u.id] && matches(u) && !availOk(u)).sort(byName) : [];
  const availableCount = pool.filter(u => availableIds[u.id]).length;
  const filtersActive = !!(q || filters.free || filters.docs || filters.license || filters.avail !== 'declaredFree'
    || filters.gender || filters.age || filters.veteran || filters.together);

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">{seesAllEv ? 'Eventy' : 'Moje eventy'}</h2>
        <div className="flex gap-6">
          {user?.role === 'EVENT' && <button className="btn btn-warning" title="Dodatek niezwiązany z jednym eventem — np. rozpakowanie kontenera, transport" onClick={() => setExpenseFor({ general: true })}>+ Dodatek</button>}
          {createsEvents && dmuch?.configured && (
            <button className="btn btn-outline" onClick={syncDmuch} disabled={dmuchBusy}
              title={`Pobierz grupy wyjazdowe z DmuchApp${dmuch.lastSync ? ` — ostatnio ${new Date(dmuch.lastSync).toLocaleString('pl-PL')}: ${dmuch.lastResult}` : ''}${dmuch.syncMinutes ? ` (automatycznie co ${dmuch.syncMinutes} min)` : ''}`}>
              {dmuchBusy ? 'Pobieranie…' : '⟳ DmuchApp'}
            </button>
          )}
          {createsEvents && <button className="btn btn-primary" onClick={openNew}>+ Dodaj event</button>}
        </div>
      </div>

      {splitView && <ScopeTabs scope={view} onChange={(v) => { setView(v); setTab('upcoming'); setEmployee(''); }} mineCount={events.filter(isMine).length} />}
      {!seesAllEv && (events.some(hasRejected) || myBonuses.some(b => b.status === 'REJECTED')) && (
        <div className="card" style={{ border: '2px solid #FCA5A5', background: '#FEF2F2', padding: '10px 14px', fontSize: 13, color: '#991B1B' }}>
          ❌ <b>Masz odrzucone rozliczenia</b> ({events.filter(hasRejected).length + myBonuses.filter(b => b.status === 'REJECTED').length}) — popraw je i wyślij ponownie (powód przy wpisie).
          {tab !== 'rejected' && events.some(hasRejected) && <button className="btn btn-danger btn-sm" style={{ marginLeft: 8 }} onClick={() => setTab('rejected')}>Pokaż odrzucone</button>}
        </div>
      )}
      <div className="filter-pills">
        {TABS.map(([v, l]) => (
          <button key={v} className={`filter-pill ${tab === v ? 'active' : ''}`} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>
      {viewSum && (viewSum.ok > 0 || viewSum.wait > 0) && (
        <div style={{ fontSize: 12, color: '#334155' }}>
          W tym widoku: <b>{viewSum.ok.toFixed(0)} zł</b> zatwierdzone
          {viewSum.wait > 0 && <span style={{ color: '#94A3B8' }}> · {viewSum.wait.toFixed(0)} zł czeka na zatwierdzenie</span>}
        </div>
      )}

      <DateFilter from={df.from} to={df.to} onFromChange={df.setFrom} onToChange={df.setTo} onFilter={load} quickFilter={df.quick} onQuickFilter={df.handleQuick}>
        {seesAllEv && <EmployeeFilter people={people} value={employee} onChange={setEmployee} />}
      </DateFilter>

      <div className="card">
        {loading ? <p style={{ color: '#94A3B8', padding: 20 }}>Ładowanie...</p> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr>
                <SortTh label="Data" k="date" sort={es.sort} onSort={es.toggle} />
                <SortTh label="Nazwa" k="name" sort={es.sort} onSort={es.toggle} />
                <SortTh label="Lokalizacja" k="location" sort={es.sort} onSort={es.toggle} />
                <th>Czas</th>
                <SortTh label="Skład" k="team" sort={es.sort} onSort={es.toggle} />
                <SortTh label="Status" k="status" sort={es.sort} onSort={es.toggle} />
                <th>Akcja</th>
              </tr></thead>
              <tbody>
                {es.sorted.map(ev => {
                  const mine = myHoursFor(ev);
                  return (
                    <tr key={ev.id}>
                      <td>{new Date(ev.date).toLocaleDateString('pl-PL')}</td>
                      <td><b>{ev.name}</b>
                        {ev.externalId && seesAllEv && <span title="Grupa wyjazdowa z DmuchApp — dane eventu aktualizują się stamtąd" style={{ marginLeft: 6, fontSize: 10, color: '#0369A1', fontWeight: 600 }}>🗺 DmuchApp</span>}
                        {ev.externalGone && seesAllEv && <div style={{ fontSize: 10, color: '#DC2626', fontWeight: 600 }} title="Grupy nie ma już w DmuchApp, a event ma skład albo rozliczenia — sprawdź, czy go anulować">⚠ usunięta w DmuchApp — sprawdź</div>}
                        {ev.requiresDocs && <span title="Skład musi mieć ważne dokumenty (Kamilkowa, niekaralność)" style={{ marginLeft: 6, fontSize: 10, color: '#7C3AED', fontWeight: 600 }}>🪪 dokumenty</span>}</td>
                      <td>{ev.location || '—'}</td>
                      <td>{ev.startTime ? `${ev.startTime} – ${ev.endTime || '?'}` : '—'}</td>
                      <td style={{ fontSize: 11 }}>
                        {ev.assignments?.length ? (
                          <span title={ev.assignments.map(a => fullName(a.user)).join(', ')}>
                            {ev.assignments.length} os.{ev.requiresDocs && ev.assignments.some(a => !a.docsVerified) && <span style={{ color: '#DC2626' }} title="Ktoś w składzie bez ważnych dokumentów na dzień eventu"> ⚠</span>}
                            {seesAllEv && (() => {
                              const withHours = new Set((ev.workHours || []).filter(h => h.status !== 'REJECTED').map(h => h.userId)); // T148
                              const total = ev.assignments.length;
                              // Event odbyty (albo dodany wstecz): nie pytamy o potwierdzenie, liczy się wpis godzin
                              if (dateKey(ev.date) < today) {
                                const n = ev.assignments.filter(a => withHours.has(a.userId)).length;
                                return <div style={{ fontSize: 10, color: n < total ? '#B45309' : '#059669' }} title="Ile osób ze składu wpisało godziny">⏱ godziny {n} / {total}</div>;
                              }
                              const ok = ev.assignments.filter(a => a.confirmedAt || withHours.has(a.userId)).length;
                              const no = ev.assignments.filter(a => a.declinedAt && !withHours.has(a.userId)).length;
                              return <div style={{ fontSize: 10, color: no ? '#DC2626' : '#64748B' }} title="Potwierdzenia udziału">✓ {ok}{no ? ` · ✗ ${no}` : ''} / {total}</div>;
                            })()}
                          </span>
                        ) : <span style={{ color: '#94A3B8' }}>brak</span>}
                      </td>
                      <td>
                        {createsEvents ? (
                          <select value={ev.status} style={{ fontSize: 11, padding: '3px 6px' }}
                            onChange={e => act(() => api.updateEventStatus(ev.id, e.target.value), 'Status zmieniony')}>
                            {EVENT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        ) : <Badge status={ev.status} />}
                      </td>
                      <td>
                        <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setDetails(ev)}>Szczegóły</button>
                          {canReviewHours && pendingOf(ev).length > 0 && (
                            <button className="btn btn-warning btn-sm" title="Godziny i wydatki składu czekające na zatwierdzenie" onClick={() => setDetails(ev)}>⏳ {pendingOf(ev).length} do zatwierdzenia</button>
                          )}
                          {buildsTeams && <button className="btn btn-outline btn-sm" title={createsEvents ? 'Edytuj' : 'Zmień skład'} onClick={() => openEdit(ev)}>{createsEvents ? '✎' : '👥'}</button>}
                          {isAdmin && <button className="btn btn-outline btn-sm" title="Usuń" onClick={() => handleDelete(ev)}>🗑</button>}
                          {isMine(ev) && dateKey(ev.date) >= today && ev.status !== 'CANCELLED' && (() => {
                            const a = myAssignment(ev);
                            // T103 (12.10): potwierdzić / odwołać można do terminu (domyślnie 48 h przed startem) — potem tylko telefon do koordynatora
                            const until = ev.confirmUntil ? new Date(ev.confirmUntil) : null;
                            const closed = until && until < new Date();
                            const untilTxt = until ? until.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                            const closedNote = <span style={{ fontSize: 10, color: '#94A3B8', display: 'block' }}>termin zmian minął — zadzwoń do koordynatora</span>;
                            if (a?.confirmedAt) return (
                              <span style={{ fontSize: 10, color: '#059669', alignSelf: 'center' }}>
                                ✓ potwierdzony udział
                                {closed ? closedNote : <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '0 4px' }} title={until ? `Odwołać można do ${untilTxt}` : undefined}
                                  onClick={() => { setDeclining(ev); setDeclineReason(''); }}>jednak nie mogę</button>}
                              </span>
                            );
                            if (a?.declinedAt) return <span style={{ fontSize: 10, color: '#DC2626', alignSelf: 'center' }} title={a.declineReason}>✗ zgłoszono: nie mogę</span>;
                            if (closed) return (<>
                              <button className="btn btn-warning btn-sm" title="Termin minął, ale potwierdzić wciąż możesz — koordynator zobaczy, że po terminie" onClick={() => confirmMine(ev)}>Potwierdzam (po terminie)</button>
                              <span style={{ fontSize: 10, color: '#B45309', alignSelf: 'center' }}>⏳ termin minął — nie możesz? zadzwoń do koordynatora</span>
                            </>); // T164
                            return (<>
                              <button className="btn btn-success btn-sm" onClick={() => confirmMine(ev)}>Potwierdzam</button>
                              <button className="btn btn-outline btn-sm" onClick={() => { setDeclining(ev); setDeclineReason(''); }}>Nie mogę</button>
                              {until && <span style={{ fontSize: 10, color: '#94A3B8', alignSelf: 'center' }}>do {untilTxt}</span>}
                            </>);
                          })()}
                          {canSettle(ev) && (
                            mine.length
                              ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {mine.map(h => h.status === 'REJECTED' ? (
                                    <RejectedNote key={h.id} item={h} onFix={myActive(ev).length ? null : () => setSettleFor({ ev, entry: h })} />
                                  ) : (
                                    <div key={h.id} style={{ fontSize: 10 }}>
                                      ⏱ {h.workType === 'EVENT_DAILY' ? 'dniówka' : `${Number(h.totalHours)} h`} · {Number(h.totalAmount).toFixed(0)} zł · <ReviewedBy item={h} short />
                                      {h.status === 'PENDING' && !h.settlementId && <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '0 4px' }} onClick={() => setSettleFor({ ev, entry: h })}>✎ popraw</button>}
                                    </div>
                                  ))}
                                </div>
                              )
                              : <button className="btn btn-success btn-sm" onClick={() => settleHours(ev)}>Rozlicz godziny</button>
                          )}
                          {canSettle(ev) && myB(ev).map(b => b.status === 'REJECTED' ? (
                            <RejectedNote key={b.id} item={b} what="Wydatek" onFix={b.settlementId ? null : () => setExpenseEdit({ ev, bonus: b })} />
                          ) : (
                            <div key={b.id} style={{ fontSize: 10 }}>
                              🧾 {BONUS_LABELS[b.bonusType] || b.bonusType} · {Number(b.amount).toFixed(0)} zł · <ReviewedBy item={b} short />
                            </div>
                          ))}
                          {canSettle(ev) && <button className="btn btn-ghost btn-sm" onClick={() => setExpenseFor(ev)} title="Paliwo, zakupy, parking — z paragonem">🧾 Zgłoś wydatek</button>}
                          {isMine(ev) && settleOpen(ev) && <button className="btn btn-ghost btn-sm" onClick={() => setShowNotes(ev)}>📝 Uwagi</button>}
                          {isMine(ev) && ev.status !== 'CANCELLED' && !settleOpen(ev) && dateKey(ev.date) === today && ev.settleFrom && (
                            <span style={{ fontSize: 10, color: '#94A3B8' }}>⏱ rozliczysz od {new Date(ev.settleFrom).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {es.sorted.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>
                  {tab === 'upcoming' ? 'Brak nadchodzących eventów w tym zakresie' : 'Brak eventów'}
                </td></tr>}
              </tbody>
            </table>
            {buildsTeams && <div style={{ fontSize: 10, color: '#64748B', marginTop: 6 }}>🪪 dokumenty = event wymaga Kamilkowej i niekaralności · ⚠ = ktoś w składzie takiego eventu nie ma ważnych dokumentów na jego dzień (szczegóły po kliknięciu „Szczegóły")</div>}
          </div>
        )}
      </div>

      {/* ---- Formularz eventu ---- */}
      {editing && (
        <Modal title={editing.id ? `${createsEvents ? 'Edytuj' : 'Skład'} — ${editing.name}` : 'Nowy event'} onClose={closeForm} size="xl" maximizable dirty={isDirty(form, formStart)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {editing.externalId && (
              <div style={{ fontSize: 12, background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '8px 10px', color: '#075985' }}>
                🗺 Event z DmuchApp — <b>nazwa, data, godziny, miejsce i opis</b> aktualizują się z DmuchApp przy każdym pobraniu (zmiany wprowadzaj tam).
                Tutaj układasz <b>skład</b> i zmieniasz status.
              </div>
            )}
            {!createsEvents && (
              <div style={{ fontSize: 12, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 10px', color: '#475569' }}>
                Masz uprawnienie <b>„przypisuje do eventów"</b> — zmieniasz wyłącznie skład. Dane eventu (nazwę, datę, miejsce, status) zmienia osoba zarządzająca eventami.
              </div>
            )}
            <fieldset disabled={!createsEvents} style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12, opacity: createsEvents ? 1 : 0.6 }}>
            <div className="flex gap-8 flex-wrap">
              <div className="input-group flex-1" style={{ minWidth: 200 }}><label>Nazwa<span style={{ color: '#DC2626' }}> *</span></label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="np. Wesele Kamińscy, Festyn Poznań" /></div>
              <div className="input-group" style={{ minWidth: 140 }}><label>Data<span style={{ color: '#DC2626' }}> *</span></label><input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            </div>
            <div className="flex gap-8 flex-wrap">
              <div className="input-group" style={{ minWidth: 110 }}><label>Start</label><TimeSelect value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} /></div>
              <div className="input-group" style={{ minWidth: 110 }}><label>Koniec</label><TimeSelect value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} /></div>
              <div className="input-group flex-1" style={{ minWidth: 180 }}><label>Lokalizacja</label><input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="adres / miejscowość" /></div>
              {editing.id && (
                <div className="input-group" style={{ minWidth: 140 }}><label>Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>{EVENT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                </div>
              )}
            </div>
            <div className="input-group"><label>Uwagi organizacyjne</label><textarea rows={6} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder={'Każda informacja w osobnej linii, np.\nGodziny: 13–18 (got. 12:45)\nAtrakcje: …\nAdres: …\nUwagi: trawa, 230 V'} />
              {/* T158: opis widzi cały skład eventu */}
              <div style={{ fontSize: 10, color: '#B45309', marginTop: 2 }}>Widzi to cały skład. Nie wklejaj kwot, danych do faktury ani e-maila klienta.</div></div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.requiresDocs} onChange={e => setForm({ ...form, requiresDocs: e.target.checked })} />
              <span>🪪 <b>Wymagane dokumenty</b> (Ustawa Kamilkowa, niekaralność) — zaznacz, gdy to konieczne</span>
            </label>
            </fieldset>

            <div className="card" style={{ background: '#F8FAFC', padding: 12 }}>
              <h4 style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700 }}>Skład ({Object.keys(form.team).length})</h4>
              <div style={{ fontSize: 10, color: '#64748B', marginBottom: 6 }}>
                Eventowi oraz osoby z włączoną pracą eventową.
                {form.requiresDocs ? ' Dokumenty sprawdzane na dzień eventu — osoba bez ważnej Kamilkowej lub niekaralności jest oznaczona ✗ (możesz ją dodać, ale to Twoja decyzja).' : ' Ten event nie wymaga dokumentów — nie sprawdzamy ich.'}
              </div>
              <div className="filter-pills" style={{ marginBottom: 8 }}>
                <button type="button" className={`filter-pill ${onlyTeam ? 'active' : ''}`} onClick={() => setOnlyTeam(true)}>👥 W składzie ({Object.keys(form.team).length})</button>
                <button type="button" className={`filter-pill ${!onlyTeam ? 'active' : ''}`} onClick={() => setOnlyTeam(false)}>➕ Dodaj osoby</button>
              </div>
              {!onlyTeam && <div className="flex gap-6 flex-wrap" style={{ alignItems: 'center', marginBottom: 8, fontSize: 11 }}>
                <input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} placeholder="🔍 imię lub nazwisko" style={{ fontSize: 11, padding: '4px 8px', minWidth: 160 }} />
                <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={filters.free} onChange={e => setFilters({ ...filters, free: e.target.checked })} /> wolni tego dnia</label>
                <select value={filters.avail} onChange={e => setFilters({ ...filters, avail: e.target.value })} style={{ fontSize: 11, padding: '3px 6px' }} title="Wg kalendarza dostępności pracownika">
                  <option value="">dostępność: dowolna</option>
                  <option value="declared">dostępni tego dnia</option>
                  <option value="declaredFree">dostępni i wolni</option>
                </select>
                <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={filters.docs} onChange={e => setFilters({ ...filters, docs: e.target.checked })} /> z ważnymi dokumentami</label>
                <select value={filters.license} onChange={e => setFilters({ ...filters, license: e.target.value })} style={{ fontSize: 11, padding: '3px 6px' }}>
                  <option value="">prawo jazdy: dowolne</option>
                  {LICENSE_CATEGORIES.map(c => <option key={c} value={c}>prawo jazdy: {c}</option>)}
                </select>
                <select value={filters.gender} onChange={e => setFilters({ ...filters, gender: e.target.value })} style={{ fontSize: 11, padding: '3px 6px' }}>
                  <option value="">płeć: dowolna</option><option value="K">kobiety</option><option value="M">mężczyźni</option>
                </select>
                <select value={filters.age} onChange={e => setFilters({ ...filters, age: e.target.value })} style={{ fontSize: 11, padding: '3px 6px' }} title="Wiek liczony na dzień eventu">
                  <option value="">wiek: dowolny</option><option value="adult">pełnoletni</option><option value="minor">nieletni</option>
                </select>
                <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }} title={`Co najmniej ${VETERAN_MIN} odbytych eventów w aplikacji albo „doświadczony” w profilu`}><input type="checkbox" checked={filters.veteran} onChange={e => setFilters({ ...filters, veteran: e.target.checked })} /> sprawdzeni (≥ {VETERAN_MIN})</label>
                <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: chosenIds.length ? 'pointer' : 'not-allowed', opacity: chosenIds.length ? 1 : 0.5 }}
                  title={chosenIds.length ? 'Osoby, które w ostatnim półroczu pracowały z już zaznaczonymi' : 'Najpierw zaznacz kogoś w składzie'}>
                  <input type="checkbox" disabled={!chosenIds.length} checked={filters.together} onChange={e => setFilters({ ...filters, together: e.target.checked })} /> ostatnio razem
                </label>
                {filtersActive && <button className="btn btn-ghost btn-sm" onClick={() => setFilters(emptyFilters())}>wyczyść</button>}
                <span style={{ color: '#94A3B8', marginLeft: 'auto' }}>do dodania: {restMatching.length} os. · dostępnych tego dnia: {availableCount}</span>
              </div>}
              {!onlyTeam && (noBirthDate > 0 || noGender > 0) && (
                <div style={{ fontSize: 10, color: '#B45309', marginBottom: 6 }}>
                  {noBirthDate > 0 && <>Filtr wieku pomija {noBirthDate} os. bez daty urodzenia (uzupełnią przy pierwszym logowaniu lub w profilu). </>}
                  {noGender > 0 && <>Filtr płci pomija {noGender} os. bez wpisanej płci (profil pracownika).</>}
                </div>
              )}
              {(onlyTeam ? inTeam.length === 0 : restMatching.length === 0 && inTeam.length === 0) && <div style={{ fontSize: 11, color: '#94A3B8' }}>{onlyTeam ? 'Nikogo jeszcze nie ma w składzie — przejdź do „Dodaj osoby”.' : filtersActive ? 'Nikt nie pasuje do filtrów.' : 'Brak osób do przypisania (nikt nie ma włączonej pracy eventowej).'}</div>}
              {/* T167: najpierw przypisani, potem reszta pasująca do filtrów — dwie osobne sekcje */}
              <div style={{ maxHeight: onlyTeam ? 'none' : 420, overflowY: 'auto', overflowX: 'hidden' }}>
                {inTeam.length > 0 && !onlyTeam && <div style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF', margin: '2px 0 4px' }}>👥 W składzie ({inTeam.length})</div>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 6 }}>
                  {inTeam.map(u => <CandidateRow key={u.id} u={u} form={form} availableIds={availableIds} busy={busy} busyText={busyText} toggleMember={toggleMember} setPayment={setPayment} toggleRole={toggleRole} />)}
                </div>
                {!onlyTeam && <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', margin: '10px 0 4px', borderTop: inTeam.length ? '1px dashed #CBD5E1' : 'none', paddingTop: inTeam.length ? 6 : 0 }}>➕ Do dodania — {restMatching.length} os.{filtersActive ? ' wg filtrów' : ''}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 6 }}>
                    {restMatching.map(u => <CandidateRow key={u.id} u={u} form={form} availableIds={availableIds} busy={busy} busyText={busyText} toggleMember={toggleMember} setPayment={setPayment} toggleRole={toggleRole} />)}
                  </div>
                </>}
              </div>
              {others.length > 0 && (
                <div style={{ marginTop: 8, borderTop: '1px dashed #CBD5E1', paddingTop: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowOthers(o => !o)} style={{ fontSize: 11 }}>
                    {showOthers ? '▲' : '▼'} Bez deklaracji dostępności ({others.length}) — możesz dodać, ale nie zgłosili tego dnia
                  </button>
                  {showOthers && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 4, maxHeight: 200, overflowY: 'auto', opacity: 0.85 }}>
                      {others.map(u => <CandidateRow key={u.id} u={u} form={form} availableIds={availableIds} busy={busy} busyText={busyText} toggleMember={toggleMember} setPayment={setPayment} toggleRole={toggleRole} />)}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={closeForm}>Anuluj</button>
              <button className="btn btn-primary" onClick={() => submit(true)} disabled={!form.name.trim() || !form.date}>{editing.id ? 'Zapisz zmiany' : 'Utwórz event'}</button>
            </div>
          </div>
        </Modal>
      )}

      {personCard && <PersonEventsModal userId={personCard.id} name={fullName(personCard)} onClose={() => setPersonCard(null)} />}

      {/* ---- Szczegóły ---- */}
      {details && (
        <Modal title={details.name} onClose={() => setDetails(null)} wide>
          <EventDetails ev={details} showAll={seesAllEv} me={user} canReview={canReviewHours} onReview={reviewInDetails}
            onPerson={canSeePay ? (u) => { setDetails(null); setPersonCard(u); } : null} />
        </Modal>
      )}

      {declining && (() => {
        // T64a: ostrzeżenie mocniejsze, gdy pracownik już potwierdził albo zostało mało czasu
        const hadConfirmed = !!myAssignment(declining)?.confirmedAt;
        const daysLeft = Math.ceil((new Date(dateKey(declining.date)) - new Date(today)) / 86400000);
        const soon = daysLeft <= 7;
        return (
        <Modal title={`${hadConfirmed ? 'Rezygnacja' : 'Nie mogę'} — ${declining.name}`} onClose={() => setDeclining(null)} dirty={!!declineReason}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {hadConfirmed && (
              <div style={{ fontSize: 13, background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 8, padding: '10px 12px' }}>
                <b>Czy na pewno chcesz teraz zrezygnować, skoro wcześniej się zgodziłeś?</b><br />
                {soon
                  ? <>Do eventu zostało {daysLeft <= 0 ? 'mniej niż dzień' : `${daysLeft} dni`} i możemy nikogo nie znaleźć na Twoje miejsce.</>
                  : <>Im później zrezygnujesz, tym trudniej znaleźć kogoś na Twoje miejsce.</>}
              </div>
            )}
            <div style={{ fontSize: 12, color: '#475569' }}>Zostajesz w składzie, dopóki koordynator Cię nie zdejmie — zobaczy Twój powód i znajdzie zastępstwo.</div>
            <div className="input-group"><label>Powód</label><textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="np. L4, wyjazd rodzinny, egzamin" />
              {declineReason.trim().length < 2 && <div style={{ fontSize: 10, color: '#B45309', marginTop: 2 }}>Wpisz krótko powód (co najmniej 2 znaki) — wtedy przycisk się odblokuje.</div>}</div>
            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={() => setDeclining(null)}>Anuluj</button>
              <button className="btn btn-danger" disabled={declineReason.trim().length < 2} onClick={doDecline}>
                {hadConfirmed ? 'Tak, rezygnuję' : 'Zgłoś, że nie mogę'}
              </button>
            </div>
          </div>
        </Modal>
        );
      })()}

      {settleFor && (
        <SettleModal ev={settleFor.ev} entry={settleFor.entry} me={user} assignment={(settleFor.ev.assignments || []).find(a => a.userId === user?.id || a.user?.id === user?.id)}
          onClose={() => setSettleFor(null)} onSaved={(msg) => { show(msg); setSettleFor(null); load(); }} onError={(m) => show(m, 'error')} />
      )}
      {user?.role === 'EVENT' && myBonuses.length > 0 && (
        <div className="card">
          <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>🧾 Moje dodatki spoza eventów</h3>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead><tr><th>Data</th><th>Rodzaj</th><th>Kwota</th><th>Opis</th><th>Status</th></tr></thead>
            <tbody>
              {myBonuses.map(b => (
                <tr key={b.id}>
                  <td>{new Date(b.date).toLocaleDateString('pl-PL')}</td>
                  <td>{BONUS_LABELS[b.bonusType] || b.bonusType}</td>
                  <td>{Number(b.amount).toFixed(0)} zł</td>
                  <td style={{ color: '#64748B' }}>{b.description || '—'}</td>
                  <td>{b.status === 'REJECTED' ? <RejectedNote item={b} what="Dodatek" onFix={() => setExpenseEdit({ ev: null, bonus: b })} /> : <ReviewedBy item={b} short />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expenseEdit && (
        <ExpenseModal ev={expenseEdit.ev} bonus={expenseEdit.bonus} onClose={() => setExpenseEdit(null)}
          onSaved={(msg) => { show(msg); setExpenseEdit(null); load(); }} onError={(m) => show(m, 'error')} />
      )}
      {expenseFor && (
        <ExpenseModal ev={expenseFor.general ? null : expenseFor} onClose={() => setExpenseFor(null)}
          onSaved={(msg) => { show(msg); setExpenseFor(null); load(); }} onError={(m) => show(m, 'error')} />
      )}

      {/* ---- Uwagi po realizacji ---- */}
      {showNotes && (
        <Modal title={`${editNote ? 'Popraw uwagi' : 'Uwagi'} — ${showNotes.name}`} onClose={() => { setShowNotes(null); setEditNote(null); setNoteForm({ content: '', rating: 5, equipmentNotes: '' }); }} dirty={!editNote && !!(noteForm.content.trim() || noteForm.equipmentNotes.trim())}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(() => { // T166: moje uwagi z ostatniej godziny — można poprawić
              const mineNotes = (showNotes.eventNotes || []).filter(n => (n.user?.id || n.userId) === user?.id && Date.now() - new Date(n.createdAt).getTime() < 60 * 60000);
              if (!mineNotes.length || editNote) return editNote ? <div style={{ fontSize: 11, color: '#475569' }}>Poprawiasz uwagi wpisane {new Date(editNote.createdAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })} — zmiany możliwe do {new Date(new Date(editNote.createdAt).getTime() + 3600000).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}.</div> : null;
              return mineNotes.map(n => (
                <div key={n.id} style={{ fontSize: 11, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6, padding: '6px 8px', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ flex: 1 }}>Twoje uwagi z {new Date(n.createdAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}: „{String(n.content).slice(0, 80)}{String(n.content).length > 80 ? '…' : ''}”</span>
                  <button className="btn btn-outline btn-sm" onClick={() => { setEditNote(n); setNoteForm({ content: n.content || '', rating: n.rating || 5, equipmentNotes: n.equipmentNotes || '' }); }}>✎ Popraw</button>
                </div>
              ));
            })()}
            <div className="input-group"><label>Przebieg realizacji</label><textarea value={noteForm.content} onChange={e => setNoteForm({ ...noteForm, content: e.target.value })} placeholder="Jak poszło..." /></div>
            <div className="input-group"><label>Ocena (1-5)</label>
              <select value={noteForm.rating} onChange={e => setNoteForm({ ...noteForm, rating: Number(e.target.value) })}>
                {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{'⭐'.repeat(r)}</option>)}
              </select>
            </div>
            <div className="input-group"><label>Stan sprzętu</label><textarea value={noteForm.equipmentNotes} onChange={e => setNoteForm({ ...noteForm, equipmentNotes: e.target.value })} placeholder="Uszkodzenia, braki..." /></div>
            <div className="flex gap-6 justify-end"><button className="btn btn-outline" onClick={() => { setShowNotes(null); setEditNote(null); setNoteForm({ content: '', rating: 5, equipmentNotes: '' }); }}>Anuluj</button><button className="btn btn-primary" onClick={submitNote} disabled={!noteForm.content.trim()}>{editNote ? 'Zapisz poprawkę' : 'Zapisz'}</button></div>
          </div>
        </Modal>
      )}
      {ToastComponent}
    </div>
  );
}

// Typy pracy pod eventem — T135 (17.10): dniówka u każdego z pracą na eventach (lustro allowedWorkTypes w routes/hours.js)
const eventWorkTypes = () => ['EVENT', 'EVENT_DAILY'];
const calcHours = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number), [eh, em] = end.split(':').map(Number);
  let min = (eh * 60 + em) - (sh * 60 + sm);
  if (min <= 0) min += 24 * 60;
  return Math.round((min / 60) * 4) / 4;
};

/** T86: rozliczenie godzin pod eventem — w oknie, bez przechodzenia do „Godzin pracy". */
function SettleModal({ ev, entry, me, assignment, onClose, onSaved, onError }) {
  // T144: rodzaj rozliczenia ustala koordynator w składzie (dniówka / godzinowo) — pracownik go nie zmienia
  const locked = assignment?.paymentType ? [assignment.paymentType === 'daily' ? 'EVENT_DAILY' : 'EVENT'] : null;
  const types = locked || eventWorkTypes(me?.role);
  const defType = entry?.workType || (assignment?.paymentType === 'daily' && types.includes('EVENT_DAILY') ? 'EVENT_DAILY' : 'EVENT');
  const init = {
    workType: types.includes(defType) ? defType : types[0],
    startTime: entry?.startTime || ev.startTime || '10:00',
    endTime: entry?.endTime || ev.endTime || '18:00',
    notes: entry?.notes || '',
  };
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const daily = f.workType === 'EVENT_DAILY';
  const hrs = daily ? null : calcHours(f.startTime, f.endTime);
  const save = async () => {
    setBusy(true);
    const body = {
      date: dateKey(ev.date), workType: f.workType, eventId: ev.id, notes: f.notes,
      ...(daily ? {} : { startTime: f.startTime, endTime: f.endTime }),
    };
    try {
      if (entry) await api.updateHours(entry.id, body);
      else await api.createHours(body);
      // T85: jeśli rozlicza się z opóźnieniem — powiedz to wprost (zatwierdzający też to zobaczy)
      const late = dateKey(ev.date) !== todayKey();
      onSaved(entry ? 'Poprawione.' : late ? `Zapisano godziny z ${fmtDay(ev.date)} — dodane dziś (${fmtDay(new Date())}).` : 'Zapisano godziny.');
    } catch (e) { onError(e.message); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm('Usunąć ten wpis godzin?')) return;
    try { await api.deleteHours(entry.id); onSaved('Wpis usunięty.'); } catch (e) { onError(e.message); }
  };
  return (
    <Modal title={`${entry ? 'Popraw rozliczenie' : 'Rozlicz godziny'} — ${ev.name}`} onClose={onClose} dirty={isDirty(f, init)}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12, color: '#475569' }}>📅 {new Date(ev.date).toLocaleDateString('pl-PL')}{ev.location ? ` · 📍 ${ev.location}` : ''}{ev.startTime ? ` · plan ${ev.startTime}–${ev.endTime || '?'}` : ''}</div>
        {locked && (
          <div style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, background: '#F1F5F9', color: '#334155' }}>
            Rozliczenie: <b>{locked[0] === 'EVENT_DAILY' ? 'dniówka' : 'godzinowo'}</b> <span style={{ color: '#64748B' }}>— ustawione przez koordynatora</span>
          </div>
        )}
        {types.length > 1 && (
          <div className="input-group"><label>Rozliczenie</label>
            <select value={f.workType} onChange={e => setF({ ...f, workType: e.target.value })}>
              <option value="EVENT">godzinowo</option><option value="EVENT_DAILY">dniówka</option>
            </select>
          </div>
        )}
        {!daily && (
          <div className="flex gap-8">
            <div className="input-group flex-1"><label>Start</label><TimeSelect value={f.startTime} onChange={e => setF({ ...f, startTime: e.target.value })} /></div>
            <div className="input-group flex-1"><label>Koniec</label><TimeSelect value={f.endTime} onChange={e => setF({ ...f, endTime: e.target.value })} /></div>
          </div>
        )}
        <div style={{ fontSize: 13 }}>{daily ? 'Dniówka — stała kwota za dzień.' : <>Razem: <b>{hrs} h</b> (bez odejmowania przerwy)</>}</div>
        <div className="input-group"><label>Uwagi (opcjonalnie)</label><input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} placeholder="np. zostałem dłużej przy demontażu" /></div>
        <div className="flex gap-6 justify-end">
          {entry && <button className="btn btn-ghost" onClick={remove} style={{ marginRight: 'auto', color: '#DC2626' }}>🗑 Usuń wpis</button>}
          <button className="btn btn-outline" onClick={onClose}>Anuluj</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || (!daily && !hrs)}>{busy ? 'Zapisywanie…' : 'Zapisz'}</button>
        </div>
      </div>
    </Modal>
  );
}

/** T86: wydatek pod eventem (paliwo, zakupy) — z paragonem, data = data eventu. */
const EXPENSE_TYPES = ['EXPENSE', 'TRANSPORT', 'OTHER'];
// T96: bez eventu (ev = null) — „+ Dodatek": z datą i dodatkowo „Rozpakowanie kontenera"; premii uznaniowej pracownik nie zgłasza
const GENERAL_TYPES = ['CONTAINER', 'TRANSPORT', 'EXPENSE', 'OTHER'];
// T153: bonus = poprawa odrzuconego wydatku / dodatku (po zapisie wraca do „Oczekuje”)
function ExpenseModal({ ev, bonus, onClose, onSaved, onError }) {
  const init = bonus
    ? { bonusType: bonus.bonusType, amount: String(Number(bonus.amount)), description: bonus.description || '', date: dateKey(bonus.date) }
    : { bonusType: ev ? 'EXPENSE' : 'CONTAINER', amount: '', description: '', date: todayKey() };
  const [f, setF] = useState(init);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const needsDesc = BONUS_NEEDS_DESC.includes(f.bonusType);
  const descOk = !needsDesc || f.description.trim().length >= BONUS_MIN_DESC;
  const tooBig = Number(f.amount) > BONUS_MAX_AMOUNT; // T88
  const ready = Number(f.amount) > 0 && !tooBig && descOk;
  const pick = (e) => { const x = e.target.files?.[0] || null; const err = api.checkFile(x); if (err) { onError(err); e.target.value = ''; setFile(null); } else setFile(x); };
  const save = async () => {
    setBusy(true);
    try {
      const body = ev
        ? { date: dateKey(ev.date), bonusType: f.bonusType, amount: f.amount, description: f.description, eventId: ev.id }
        : { date: f.date, bonusType: f.bonusType, amount: f.amount, description: f.description };
      const b = bonus ? await api.updateBonus(bonus.id, body) : await api.createBonus(body);
      if (file && needsDesc) {
        try { await api.uploadAttachment('BONUS', b.id, file); }
        catch (e) { onSaved(`Wydatek zapisany, ale paragon nie: ${e.message}`); return; }
      }
      onSaved(bonus ? 'Poprawione — czeka ponownie na zatwierdzenie.' : ev ? 'Wydatek zgłoszony.' : 'Dodatek zgłoszony — czeka na zatwierdzenie.');
    } catch (e) { onError(e.message); }
    setBusy(false);
  };
  return (
    <Modal title={`${bonus ? 'Popraw: ' : ''}${ev ? `Wydatek — ${ev.name}` : 'Dodatek (spoza eventu)'}`} onClose={onClose} dirty={isDirty(f, init) || !!file}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {bonus?.rejectReason && <div style={{ fontSize: 12, color: '#991B1B', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 6, padding: '6px 10px' }}>❌ Powód odrzucenia: „{bonus.rejectReason}”</div>}
        {!ev && <div className="input-group" style={{ maxWidth: 180 }}><label>Data</label><input type="date" value={f.date} max={todayKey()} onChange={e => setF({ ...f, date: e.target.value })} /></div>}
        <div className="flex gap-8">
          <div className="input-group flex-1"><label>Rodzaj</label>
            <select value={f.bonusType} onChange={e => { setF({ ...f, bonusType: e.target.value }); if (!BONUS_NEEDS_DESC.includes(e.target.value)) setFile(null); }}>
              {(ev ? EXPENSE_TYPES : GENERAL_TYPES).map(t => <option key={t} value={t}>{BONUS_LABELS[t]}</option>)}
              {bonus && !(ev ? EXPENSE_TYPES : GENERAL_TYPES).includes(f.bonusType) && <option value={f.bonusType}>{BONUS_LABELS[f.bonusType] || f.bonusType}</option>}
            </select>
          </div>
          <div className="input-group" style={{ width: 140 }}><label>Kwota (zł)</label><input type="number" min="1" max={BONUS_MAX_AMOUNT} step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="np. 85" /></div>
        </div>
        {tooBig && <div style={{ fontSize: 11, color: '#DC2626' }}>Za dużo — jeden wydatek to maksymalnie {BONUS_MAX_AMOUNT} zł. Sprawdź, czy nie ma literówki; większy podziel na kilka pozycji.</div>}
        <div className="input-group">
          <label>Opis{needsDesc && <span style={{ color: '#DC2626' }}> * (min. {BONUS_MIN_DESC} znaków)</span>}</label>
          <textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder="np. paliwo Poznań–Leszno w obie strony" />
        </div>
        {needsDesc && (
          <div className="input-group"><label>Paragon / faktura (zdjęcie lub PDF, do 5 MB)</label>
            <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment" onChange={pick} />
            {file && <div style={{ fontSize: 10, color: '#059669', marginTop: 2 }}>✓ {file.name}</div>}
          </div>
        )}
        <div className="flex gap-6 justify-end">
          <button className="btn btn-outline" onClick={onClose}>Anuluj</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !ready || (!ev && !f.date)}>{busy ? 'Wysyłanie…' : bonus ? 'Wyślij ponownie' : ev ? 'Zgłoś wydatek' : 'Zgłoś dodatek'}</button>
        </div>
      </div>
    </Modal>
  );
}

/** T138: funkcje w ekipie z przypisań eventu → { userId: { leader, driver } } */
const rolesOf = (ev) => Object.fromEntries((ev.assignments || []).filter(a => a.isLeader || a.isDriver || a.isAnimator).map(a => [a.user?.id || a.userId, { leader: !!a.isLeader, driver: !!a.isDriver, animator: !!a.isAnimator }]));
/** T138: etykiety „👑 lider” / „🚐 kierowca” przy osobie w składzie */
export const TeamRoleBadges = ({ a }) => (a?.isLeader || a?.isDriver || a?.isAnimator) ? (
  <span style={{ display: 'inline-flex', gap: 4, marginLeft: 4, verticalAlign: 'middle' }}>
    {a.isLeader && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: '#FEF3C7', color: '#92400E' }}>👑 lider</span>}
    {a.isDriver && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: '#DBEAFE', color: '#1E40AF' }}>🚐 kierowca</span>}
    {a.isAnimator && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: '#FCE7F3', color: '#BE185D' }}>🎈 animacja</span>}
  </span>
) : null;

function CandidateRow({ u, form, availableIds, busy, busyText, toggleMember, setPayment, toggleRole }) {
  const on = !!form.team[u.id];
  const role = (form.roles || {})[u.id] || {};
  const noLicense = !String(u.drivingLicense || '').split(',').map(x => x.trim()).includes('B');
  const together = Object.keys(form.team).filter(id => id !== u.id).reduce((sum, id) => sum + ((u.together || {})[id] || 0), 0);
  const d = docStatus(u, form.date);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap', fontSize: 11, padding: '5px 6px', borderRadius: 6, minWidth: 0, background: on ? '#EFF6FF' : '#fff', border: `1px solid ${on ? '#BFDBFE' : '#E2E8F0'}` }}>
      <input type="checkbox" checked={on} onChange={() => toggleMember(u)} style={{ marginTop: 2 }} />
      <span style={{ flex: '1 1 180px', minWidth: 0, overflowWrap: 'anywhere' }}>
        <b>{fullName(u)}</b> <span style={{ color: '#94A3B8' }}>({ROLE_LABELS[u.role] || u.role}{u.drivingLicense ? `, pr. jazdy ${u.drivingLicense}${u.licenseSince ? ` od ${u.licenseSince}` : ''}` : ''})</span>
        {u.adult === false && <span style={{ fontSize: 10, color: '#7C3AED', marginLeft: 4 }} title={`${u.age} lat w dniu eventu`}>🔞 {u.age} l.</span>}
        {u.experienced
          ? <span style={{ fontSize: 10, color: '#2563EB', marginLeft: 4 }} title={'Doświadczony (zaznaczone w profilu) — odbyte eventy w aplikacji: ' + (u.completedEvents || 0)}>★ doświadczony</span>
          : (u.completedEvents || 0) >= VETERAN_MIN && <span style={{ fontSize: 10, color: '#2563EB', marginLeft: 4 }} title="Odbyte eventy">★ {u.completedEvents}</span>}
        {together > 0 && <span style={{ fontSize: 10, color: '#0891B2', marginLeft: 4 }} title="Ile razy pracował z już zaznaczonymi (ostatnie pół roku)">🤝 {together}×</span>}
        {availableIds[u.id] && (() => {
          const a = availableIds[u.id];
          const late = a.fromTime && form.startTime && a.fromTime > form.startTime;
          return (
            <span style={{ fontSize: 10, color: late ? '#B45309' : '#059669', marginLeft: 6 }}
              title={[a.fromTime ? `dostępny ${a.fromTime}–${a.toTime}` : 'dostępny cały dzień', a.note].filter(Boolean).join(' · ')}>
              ✓ dostępny{a.fromTime ? ` od ${a.fromTime}` : ''}{late ? ' ⚠ później niż start eventu' : ''}{a.note ? ' 📝' : ''}
            </span>
          );
        })()}
        {u.internalNotes && <div style={{ fontSize: 10, color: '#92400E', whiteSpace: 'pre-wrap' }} title="Uwagi wewnętrzne — pracownik ich nie widzi">📝 {u.internalNotes}</div>}
        {busy[u.id] && <div style={{ fontSize: 10, color: '#DC2626' }} title={busyText(busy[u.id])}>⏰ tego dnia na: {busy[u.id].name}{busy[u.id].startTime ? ` (${busy[u.id].startTime}–${busy[u.id].endTime || '?'})` : ''}</div>}
      </span>
      {form.requiresDocs && <span style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}><DocBadge label="Kamil." state={d.kamilkowa} /> <DocBadge label="Niekar." state={d.niekaralnosc} /></span>}
      {on && toggleRole && (
        <span className="flex gap-4" style={{ flexWrap: 'wrap', width: '100%', paddingLeft: 20 }}>
          <button type="button" className={`filter-pill ${role.leader ? 'active' : ''}`} style={{ fontSize: 10, padding: '1px 6px' }} title="Lider wyjazdu — widzi to cała ekipa" onClick={() => toggleRole(u.id, 'leader')}>👑 Lider</button>
          <button type="button" className={`filter-pill ${role.driver ? 'active' : ''}`} style={{ fontSize: 10, padding: '1px 6px', ...(role.driver && noLicense ? { borderColor: '#F59E0B', color: '#B45309' } : {}) }}
            title={noLicense ? 'Kierowca — uwaga: w profilu brak prawa jazdy kat. B' : 'Kierowca — widzi to cała ekipa'} onClick={() => toggleRole(u.id, 'driver')}>🚐 Kierowca{role.driver && noLicense ? ' ⚠' : ''}</button>
          {u.animationWorkEnabled && <button type="button" className={`filter-pill ${role.animator ? 'active' : ''}`} style={{ fontSize: 10, padding: '1px 6px' }} title="Animator(ka) — czas trwania eventu liczy się stawką animacji" onClick={() => toggleRole(u.id, 'animator')}>🎈 Animator(ka)</button>}
        </span>
      )}
      {on && (
        <select value={form.team[u.id]} onChange={e => setPayment(u.id, e.target.value)} style={{ fontSize: 10, padding: '2px 4px' }} title="Sposób rozliczenia">
          <option value="hourly">godzinowo</option><option value="daily">dniówka</option>
        </select>
      )}
    </div>
  );
}

function EventDetails({ ev, showAll, me, canReview, onReview, onPerson }) {
  const hours = ev.workHours || [];
  // T77: co z tego eventu czeka na decyzję (bez własnych i rozliczonych)
  const reviewable = (x) => canReview && x.status === 'PENDING' && x.userId !== me?.id && !x.settlementId;
  const pendH = hours.filter(reviewable);
  // T99: kto ma inne godziny niż reszta składu (tylko widok rozpatrującego)
  const diffs = showAll ? teamDiffs(hours) : {};
  const pendDiff = pendH.filter(x => diffs[x.id]?.differs).length;
  const pendB = (ev.bonuses || []).filter(reviewable);
  const approveAll = () => onReview(async () => {
    const msgs = [];
    if (pendH.length) msgs.push((await api.approveBulkHours(pendH.map(x => x.id))).message);
    if (pendB.length) msgs.push((await api.approveBulkBonuses(pendB.map(x => x.id))).message);
    return { message: msgs.join(' ') };
  }, 'Zatwierdzone');
  // T95: zatwierdzone przeze mnie (albo jestem adminem) — można cofnąć zatwierdzenie
  const undoable = (x) => x.status === 'APPROVED' && !x.settlementId && (me?.role === 'ADMIN' || x.approvedBy?.id === me?.id);
  const ReviewButtons = ({ x, bonus }) => undoable(x) ? (
    <button className="btn btn-outline btn-sm" style={{ padding: '0 6px', marginLeft: 4 }} title="Cofnij zatwierdzenie — wpis wróci do „Oczekuje”"
      onClick={() => { if (window.confirm('Cofnąć zatwierdzenie? Wpis wróci do „Oczekuje”.')) onReview(() => bonus ? api.unapproveBonus(x.id) : api.unapproveHours(x.id), 'Zatwierdzenie cofnięte'); }}>↩</button>
  ) : reviewable(x) ? (
    <span style={{ marginLeft: 4, whiteSpace: 'nowrap' }}>
      <button className="btn btn-success btn-sm" style={{ padding: '0 6px' }} title="Zatwierdź" onClick={() => onReview(() => bonus ? api.approveBonus(x.id) : api.approveHours(x.id), 'Zatwierdzone')}>✓</button>{' '}
      <button className="btn btn-danger btn-sm" style={{ padding: '0 6px' }} title="Odrzuć" onClick={() => { const r = askRejectReason(); if (r !== null) onReview(() => bonus ? api.rejectBonus(x.id, r) : api.rejectHours(x.id, r), 'Odrzucone'); }}>✗</button>
    </span>
  ) : null;
  // Odbyty (albo dodany wstecz) — udział potwierdza wpis godzin, nie przycisk
  const past = dateKey(ev.date) < todayKey();
  const sum = (list) => list.reduce((acc, h) => { const amt = Number(h.totalAmount) || 0; if (h.status === 'APPROVED') acc.approved += amt; else if (h.status === 'PENDING') acc.pending += amt; return acc; }, { approved: 0, pending: 0 });
  const totals = sum(hours);
  const mineOnly = showAll ? hours : hours.filter(h => h.userId === me?.id);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 12 }}>
      <div className="flex gap-8 flex-wrap" style={{ color: '#475569' }}>
        <span>📅 {new Date(ev.date).toLocaleDateString('pl-PL')}</span>
        {ev.startTime && <span>🕒 {ev.startTime} – {ev.endTime || '?'}</span>}
        {ev.location && <span>📍 {ev.location}</span>}
        <Badge status={ev.status} />
        {ev.requiresDocs && <span style={{ color: '#7C3AED', fontWeight: 600 }}>🪪 wymagane dokumenty</span>}
      </div>
      {ev.notes && <div className="card" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: 12, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{ev.notes}</div>} {/* T158: z podziałem na linie, jak w kalendarzu */}
      {(pendH.length + pendB.length) > 0 && (
        <div className="flex gap-8" style={{ alignItems: 'center', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px' }}>
          {pendDiff > 0 && <span style={{ color: '#B45309', fontWeight: 600 }}>⚠ {pendDiff} {pendDiff === 1 ? 'osoba ma' : 'osoby mają'} inne godziny niż reszta składu ·</span>}
          <span>⏳ Czeka na zatwierdzenie: <b>{pendH.length}</b> {pendH.length === 1 ? 'wpis' : pendH.length < 5 ? 'wpisy' : 'wpisów'} godzin{pendB.length ? <>, <b>{pendB.length}</b> {pendB.length === 1 ? 'wydatek' : pendB.length < 5 ? 'wydatki' : 'wydatków'}</> : null}.</span>
          <button className="btn btn-success btn-sm" style={{ marginLeft: 'auto' }} onClick={() => { if (window.confirm(`Zatwierdzić wszystkie oczekujące wpisy pod tym eventem (${pendH.length + pendB.length})?${pendDiff ? `\n\nUWAGA: ${pendDiff} ${pendDiff === 1 ? 'osoba ma' : 'osoby mają'} inne godziny niż reszta składu — sprawdź przed zatwierdzeniem.` : ''}`)) approveAll(); }}>✓ Zatwierdź wszystkie</button>
        </div>
      )}

      <div>
        <h4 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700 }}>Skład ({ev.assignments?.length || 0})</h4>
        {ev.assignments?.length ? (
          <table className="data-table">
            <thead><tr><th>Osoba</th><th>Rola</th><th>Udział</th>{showAll && <th>Rozliczenie</th>}<th>Dokumenty</th>{showAll && <th>Godziny pod eventem</th>}</tr></thead>
            <tbody>
              {ev.assignments.map(a => {
                const d = docStatus(a.user, ev.date);
                const h = hours.filter(x => x.userId === a.user.id);
                return (
                  <tr key={a.id}>
                    <td>{onPerson
                      ? <button className="btn btn-ghost btn-sm" style={{ padding: 0, textAlign: 'left' }} title="Na jakich eventach był, godziny, wypłaty" onClick={() => onPerson(a.user)}><PersonCell name={fullName(a.user)} /></button>
                      : <PersonCell name={fullName(a.user)} />}
                      <TeamRoleBadges a={a} /></td>
                    <td><RoleBadge role={a.user.role} /></td>
                    <td style={{ fontSize: 11 }}>
                      {h.length ? <span style={{ color: '#059669' }}>✓ był — godziny wpisane</span>
                        : past ? <span style={{ color: '#B45309' }}>⏱ czeka na wpis godzin</span>
                        : a.confirmedAt ? <span style={{ color: '#059669' }}>✓ potwierdził</span>
                        : a.declinedAt ? <span style={{ color: '#DC2626' }} title={a.declineReason}>✗ nie może{a.declineReason ? `: ${a.declineReason}` : ''}</span>
                        : <span style={{ color: '#94A3B8' }}>— brak odpowiedzi</span>}
                    </td>
                    {showAll && <td style={{ fontSize: 11 }}>{a.paymentType === 'daily' ? 'dniówka' : 'godzinowo'}</td>}
                    <td style={ev.requiresDocs ? undefined : { opacity: 0.45 }} title={ev.requiresDocs ? undefined : 'Ten event nie wymaga dokumentów — informacyjnie'}><DocBadge label="Kamil." state={d.kamilkowa} /> <DocBadge label="Niekar." state={d.niekaralnosc} /></td>
                    {showAll && <td style={{ fontSize: 11 }}>
                      {h.length ? h.map(x => (
                        <div key={x.id}>
                          {x.workType === 'EVENT_DAILY' ? 'dniówka' : `${Number(x.totalHours)}h`}{x.startTime ? ` (${x.startTime}–${x.endTime})` : ''}{Number(x.animationHours) > 0 ? ` · 🎈 w tym animacja ${Number(x.animationHours)} h` : ''} · {Number(x.totalAmount).toFixed(0)} zł · <ReviewedBy item={x} /> <AddedOn date={x.date} createdAt={x.createdAt} /><ReviewButtons x={x} />
                          {x.notes && <div style={{ color: '#64748B', fontStyle: 'italic' }}>„{x.notes}”</div>}
                          <TeamDiffBadge tc={diffs[x.id]} />
                        </div>
                      )) : <span style={{ color: '#94A3B8' }}>brak wpisu</span>}
                      {(ev.bonuses || []).filter(b => b.userId === a.user.id).map(b => (
                        <div key={b.id}>🧾 {BONUS_LABELS[b.bonusType] || b.bonusType} · {Number(b.amount).toFixed(0)} zł{b.description ? ` — ${b.description}` : ''} · <ReviewedBy item={b} /><ReviewButtons x={b} bonus /></div>
                      ))}
                    </td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : <div style={{ color: '#94A3B8' }}>Nikt jeszcze nie jest przypisany.</div>}
      </div>

      {showAll && (
        <div className="flex gap-8 flex-wrap">
          <div className="card stat-card"><div className="stat-label">Koszt (zatwierdzone)</div><div className="stat-value" style={{ color: '#059669' }}>{totals.approved.toFixed(0)} zł</div></div>
          <div className="card stat-card"><div className="stat-label">Oczekuje na zatwierdzenie</div><div className="stat-value" style={{ color: '#D97706' }}>{totals.pending.toFixed(0)} zł</div></div>
          <div className="card stat-card"><div className="stat-label">Bez wpisu godzin</div><div className="stat-value" style={{ color: '#64748B' }}>{(ev.assignments || []).filter(a => !hours.some(h => h.userId === a.user.id)).length} os.</div></div>
        </div>
      )}
      {!showAll && mineOnly.length > 0 && (
        <div style={{ fontSize: 11, color: '#475569' }}>Twoje wpisy pod tym eventem: {mineOnly.map(x => `${x.workType === 'EVENT_DAILY' ? 'dniówka' : Number(x.totalHours) + 'h'} (${Number(x.totalAmount).toFixed(0)} zł)`).join(', ')}</div>
      )}

      {ev.eventNotes?.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700 }}>Uwagi po realizacji</h4>
          {ev.eventNotes.map(n => (
            <div key={n.id} className="card" style={{ padding: 8, marginBottom: 6, fontSize: 11 }}>
              <div style={{ color: '#64748B' }}>{fullName(n.user)} · {new Date(n.createdAt).toLocaleDateString('pl-PL')} {n.rating ? '⭐'.repeat(n.rating) : ''}</div>
              <div>{n.content}</div>
              {n.equipmentNotes && <div style={{ color: '#B45309' }}>Sprzęt: {n.equipmentNotes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
